// ─────────────────────────────────────────────────────────────────────
// <TopNav /> — design vision spec
//
// Layout:  [logo + wordmark]  [Movies | Events▾ | Plays | Sports | Activities | Stream(SOON)]   [in <City>▾] [Search] [Sign in]
//
// On scroll: backdrop blur 20px, semi-transparent bg.
// Active link gets gold underline.
// On mobile (<lg), collapses to drawer.
// ─────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search as SearchIcon, X, Menu, Sun, Moon } from 'lucide-react';

import BrandLogo from '../tenant/BrandLogo';            // r7.6 tenant mark
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';   // r7.6 tenant scope (hide marketplace surfaces)
import { useLocation as useCity } from '../../context/LocationContext';
import { useTheme } from '../../themes/ThemeContext';
import MegaMenu from './MegaMenu';

const NAV_LINKS = [
  { to: '/movies',       key: 'movies',     soon: true },
  { to: '/v/live-events',key: 'events',     megaKey: 'events' },
  { to: '/v/theatre',    key: 'plays' },
  { to: '/v/sports',     key: 'sports' },
  { to: '/v/attractions',key: 'activities' },
  { to: '/stream',       key: 'stream',     soon: true },
];

// r7.6 — a tenant site shows its own events only: no verticals, no mega menu, no marketplace search.
// Discovery is tenant-scoped server-side; the shell just stops advertising the marketplace.
const TENANT_LINKS = [
  { to: '/v/live-events', key: 'events' },
  { to: '/me/bookings',   key: 'tickets' },
];

export default function TopNav() {
  const { t } = useTranslation();
  const loc = useLocation();
  const { user, logout } = useAuth();
  const tenant = useTenant();                                   // r7.6
  const links = tenant.isPlatform ? NAV_LINKS : TENANT_LINKS;   // r7.6
  const { city, openCityPicker } = useCity();
  const { activeMode, toggleMode } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  return (
    <>
      <nav
        className={[
          'sticky top-0 z-40 w-full',
          'border-b border-line',
          'transition-all duration-250',
          scrolled ? 'backdrop-blur-lg' : '',
        ].join(' ')}
        style={{
          background: scrolled ? 'rgba(10,10,11,0.85)' : 'var(--snt-bg)',
        }}
        aria-label="Primary"
      >
        <div className="max-w-container mx-auto px-6 lg:px-9 flex items-center justify-between h-[68px]">
          {/* Left: logo + nav links */}
          <div className="flex items-center gap-7">
            <BrandLogo variant="full" size={36} themed />

            <ul className="hidden lg:flex items-center gap-7 ml-3">
              {links.map((link) => {
                const label = t(`nav.${link.key}`, link.key);
                const isActive = loc.pathname.startsWith(link.to);
                return (
                  <li
                    key={link.key}
                    className="relative"
                    onMouseEnter={() => link.megaKey && setMegaOpen(link.megaKey)}
                    onMouseLeave={() => link.megaKey && setMegaOpen(null)}
                  >
                    <NavLink
                      to={link.to}
                      className={[
                        'text-[13px] tracking-tight transition-colors no-underline',
                        isActive ? 'text-text' : 'text-text-2 hover:text-text',
                      ].join(' ')}
                    >
                      <span className="capitalize">{label}</span>
                      {link.soon && (
                        <span
                          className="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[9px] tracking-wider align-middle"
                          style={{
                            background: 'rgba(212,165,116,0.15)',
                            color: 'var(--snt-accent)',
                          }}
                        >
                          SOON
                        </span>
                      )}
                      {link.megaKey && (
                        <span className="ml-1 text-[10px]">▾</span>
                      )}
                      {isActive && (
                        <span
                          className="absolute -bottom-[24px] left-0 right-0 h-px"
                          style={{ background: 'var(--snt-accent)' }}
                        />
                      )}
                    </NavLink>

                    {link.megaKey && megaOpen === link.megaKey && (
                      <div className="absolute top-full left-0 pt-3 z-50">
                        <MegaMenu menuKey={link.megaKey} onSelect={() => setMegaOpen(null)} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right: city + search + auth */}
          <div className="flex items-center gap-4">
            {tenant.isPlatform && (
            <button
              onClick={openCityPicker}
              className="hidden lg:flex items-center text-[13px] text-text-2 hover:text-text transition-colors snt-btn-link"
            >
              {t('nav.in', 'In')}{' '}
              <span className="text-text mx-1.5 not-italic">{city || 'Bengaluru'}</span> ▾
            </button>
            )}

            <button
              type="button"
              onClick={toggleMode}
              className="hidden md:flex items-center justify-center w-9 h-9 rounded-full transition-colors"
              style={{
                background: 'transparent',
                border: '1px solid var(--snt-line-strong)',
                color: 'var(--snt-text-2)',
              }}
              aria-label={activeMode === 'dark' ? t('nav.switch_light', 'Switch to light mode') : t('nav.switch_dark', 'Switch to dark mode')}
              title={activeMode === 'dark' ? 'Light mode' : 'Dark mode'}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--snt-accent)';
                e.currentTarget.style.color = 'var(--snt-accent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--snt-line-strong)';
                e.currentTarget.style.color = 'var(--snt-text-2)';
              }}
            >
              {activeMode === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            {tenant.isPlatform && (
            <Link
              to="/search"
              className="snt-btn-ghost hidden md:inline-flex items-center gap-1.5 no-underline"
              aria-label={t('nav.search', 'Search')}
            >
              <SearchIcon size={13} className="opacity-70" />
              <span>{t('nav.search', 'Search')}</span>
            </Link>
            )}

            {user ? (
              <UserMenu user={user} onLogout={logout} />
            ) : (
              <Link to="/login" className="snt-btn-pri no-underline">
                {t('nav.sign_in', 'Sign in')}
              </Link>
            )}

            <button
              type="button"
              className="lg:hidden text-text-2 p-1"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <MobileDrawer
          links={links}
          onClose={() => setMobileOpen(false)}
          user={user}
        />
      )}
    </>
  );
}

// ─── User dropdown menu ───────────────────────────────────────────────
function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  // r7.6 — console entries come from the permission model, not role strings: console staff (ops/support/
  // finance/content) see Admin, tenant owners see their Branding console, organizers see the Studio.
  const { isOrganizer, isConsole, isTenantMember } = useAuth();

  useEffect(() => {
    const close = () => setOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  return (
    <div
      className="relative"
      onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
    >
      <button
        className="snt-btn-ghost flex items-center gap-1.5"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span
          className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-medium"
          style={{ background: 'var(--snt-accent)', color: 'var(--snt-accent-fg)' }}
        >
          {(user.name || user.email || '?').charAt(0).toUpperCase()}
        </span>
        <span className="hidden md:inline">{user.name?.split(' ')[0] || 'You'}</span>
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 min-w-[200px] py-2 rounded-card border border-line"
          style={{ background: 'var(--snt-bg-1)' }}
          role="menu"
        >
          <Link to="/me" className="block px-4 py-2 text-[13px] text-text-2 hover:text-text no-underline">
            {t('nav.profile', 'My account')}
          </Link>
          <Link to="/me/bookings" className="block px-4 py-2 text-[13px] text-text-2 hover:text-text no-underline">
            {t('nav.bookings', 'My bookings')}
          </Link>
          <Link to="/me/wishlist" className="block px-4 py-2 text-[13px] text-text-2 hover:text-text no-underline">
            {t('nav.wishlist', 'Wishlist')}
          </Link>
          <Link to="/me/preferences" className="block px-4 py-2 text-[13px] text-text-2 hover:text-text no-underline">
            {t('nav.preferences', 'Preferences')}
          </Link>
          <Link to="/help" className="block px-4 py-2 text-[13px] text-text-2 hover:text-text no-underline" data-testid="menu-help">
            {t('nav.help', 'Help & support')}
          </Link>
          {(isOrganizer || isConsole || isTenantMember) && (
            <>
              <div className="my-1 border-t border-line" />
              {isOrganizer && (
                <Link to="/organizer" className="block px-4 py-2 text-[13px] text-text-2 hover:text-text no-underline">
                  {t('nav.organizer', 'Organizer studio')}
                </Link>
              )}
              {isTenantMember && (
                <Link to="/organizer/branding" className="block px-4 py-2 text-[13px] text-text-2 hover:text-text no-underline">
                  {t('nav.tenant_console', 'My brand')}
                </Link>
              )}
              {isConsole && (
                <Link to="/admin" className="block px-4 py-2 text-[13px] text-text-2 hover:text-text no-underline">
                  {t('nav.admin', 'Admin')}
                </Link>
              )}
            </>
          )}
          <div className="my-1 border-t border-line" />
          <button
            onClick={onLogout}
            className="w-full text-left px-4 py-2 text-[13px] text-text-2 hover:text-text bg-transparent border-0 cursor-pointer"
          >
            {t('nav.sign_out', 'Sign out')}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Mobile drawer ────────────────────────────────────────────────────
function MobileDrawer({ links, onClose, user }) {
  const { t } = useTranslation();
  const { isOrganizer, isConsole, isTenantMember } = useAuth();   // r7.6
  return (
    <div
      className="fixed inset-0 z-50 lg:hidden"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <aside
        className="absolute right-0 top-0 h-full w-[80%] max-w-xs flex flex-col"
        style={{ background: 'var(--snt-bg-1)', borderLeft: '1px solid var(--snt-line)' }}
      >
        <div className="flex items-center justify-between p-5 border-b border-line">
          <BrandLogo variant="full" size={32} themed />
          <button
            onClick={onClose}
            className="text-text-2 p-1 bg-transparent border-0 cursor-pointer"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {links.map((link) => (
            <Link
              key={link.key}
              to={link.to}
              onClick={onClose}
              className="block px-6 py-3 text-text-2 hover:text-text no-underline"
            >
              {t(`nav.${link.key}`, link.key)}
              {link.soon && (
                <span
                  className="ml-2 inline-block px-1.5 py-0.5 rounded text-[9px] tracking-wider align-middle"
                  style={{ background: 'rgba(212,165,116,0.15)', color: 'var(--snt-accent)' }}
                >
                  SOON
                </span>
              )}
            </Link>
          ))}
          <div className="my-3 mx-6 border-t border-line" />
          {user ? (
            <>
              <Link to="/me/bookings" onClick={onClose} className="block px-6 py-3 text-text-2 no-underline">
                {t('nav.bookings', 'My bookings')}
              </Link>
              <Link to="/me/wishlist" onClick={onClose} className="block px-6 py-3 text-text-2 no-underline">
                {t('nav.wishlist', 'Wishlist')}
              </Link>
              <Link to="/me/preferences" onClick={onClose} className="block px-6 py-3 text-text-2 no-underline">
                {t('nav.preferences', 'Preferences')}
              </Link>
              <Link to="/help" onClick={onClose} className="block px-6 py-3 text-text-2 no-underline" data-testid="drawer-help">
                {t('nav.help', 'Help & support')}
              </Link>
              {(isOrganizer || isConsole || isTenantMember) && <div className="my-3 mx-6 border-t border-line" />}
              {isOrganizer && (
                <Link to="/organizer" onClick={onClose} className="block px-6 py-3 text-text-2 no-underline">{t('nav.organizer', 'Organizer studio')}</Link>
              )}
              {isTenantMember && (
                <Link to="/organizer/branding" onClick={onClose} className="block px-6 py-3 text-text-2 no-underline">{t('nav.tenant_console', 'My brand')}</Link>
              )}
              {isConsole && (
                <Link to="/admin" onClick={onClose} className="block px-6 py-3 text-text-2 no-underline">{t('nav.admin', 'Admin')}</Link>
              )}
            </>
          ) : (
            <Link to="/login" onClick={onClose} className="block mx-6 mt-2">
              <span className="snt-btn-pri w-full inline-block text-center">
                {t('nav.sign_in', 'Sign in')}
              </span>
            </Link>
          )}
        </nav>
      </aside>
    </div>
  );
}
